import { useEffect, useState } from 'react';
import * as yup from 'yup';

import { useAddUser } from '@/api/auth/addUserDetails';
import { useGetStates } from '@/api/auth/getStates';
import type { AddUserType } from '@/types/auth';
import type { State } from '@/utils';
import { StringPatterns } from '@/utils';

import { FieldWrapper, Form } from '../Form';

type UserDetailsType = {
  name: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  email: string;
  state: string;
};

const userDetailsSchema = yup.object({
  name: yup
    .string()
    .matches(StringPatterns.firstName, 'Enter valid name')
    .required('Name is required'),
  email: yup.string().email('Enter valid email').required('Email is required'),
  gender: yup.string().required('Gender is required'),
  state: yup.string().required('State is required'),
});

type UserDetailsProps = {
  closeModal: () => void;
};

const UserDetails = ({ closeModal }: UserDetailsProps) => {
  const { states, isLoading: isStatesLoading } = useGetStates();
  const [userDetails, setUserDetails] = useState<UserDetailsType>({
    name: '',
    email: '',
    gender: 'MALE',
    state: '',
  });
  const addUser = useAddUser();

  useEffect(() => {
    if (!isStatesLoading && states.length > 0) {
      setUserDetails((prevUserDetails) => ({
        ...prevUserDetails,
        state: states[0].stateName,
      }));
    }
  }, [states, isStatesLoading]);

  const splitUserName = (name: string) => {
    const fullName = name.trim().split(/\s+/);
    if (fullName.length > 1) {
      const firstName = fullName[0];
      const lastName = fullName.slice(1).join(' ');
      return { firstName, lastName };
    }
    if (fullName.length === 1) {
      const firstName = fullName[0];
      const lastName = '';
      return { firstName, lastName };
    }
    return { firstName: '', lastName: '' };
  };

  return (
    <Form<UserDetailsType, typeof userDetailsSchema>
      onSubmit={async (values) => {
        // setUserDetails({ ...values });
        const { firstName, lastName } = splitUserName(values.name);
        const addUserRequestBody: AddUserType = {
          firstName,
          ...(lastName && { lastName }),
          email: values.email,
          gender: values.gender,
          stateId: values.state,
        };
        const res = await addUser.mutateAsync(addUserRequestBody);
        if (res.status === 200) {
          setUserDetails((prevUserDetails) => ({
            ...prevUserDetails,
            name: '',
            email: '',
            gender: 'MALE',
            state: '',
          }));
          closeModal();
        }
      }}
      schema={userDetailsSchema}
      options={{
        defaultValues: {
          name: userDetails.name,
          email: userDetails.email,
          gender: userDetails.gender,
          state: userDetails.state,
        },
      }}
    >
      {({ register, formState }) => (
        <div className={`stepper--content active`}>
          <p className="login__title text--black text--bold text-left">
            Details
          </p>
          <div className="input-container">
            <FieldWrapper label={''} error={formState.errors.name}>
              <div className="input-form">
                <input
                  data-hj-allow
                  maxLength={30}
                  type="text"
                  placeholder="Enter your name"
                  {...register('name')}
                />
              </div>
            </FieldWrapper>
          </div>
          <div className="input-container">
            <FieldWrapper label={'Gender'} error={formState.errors.gender}>
              <div className="radio--row">
                <label className="gender--radio">
                  <input
                    type="radio"
                    {...register('gender')}
                    data-hj-allow
                    value={'MALE'}
                  />
                  <p className="text--bold text--semi title male">Male</p>
                </label>
                <label className="gender--radio">
                  <input
                    type="radio"
                    data-hj-allow
                    {...register('gender')}
                    value={'FEMALE'}
                  />
                  <p className="text--bold text--semi title female">Female</p>
                </label>
                <label className="gender--radio">
                  <input
                    type="radio"
                    data-hj-allow
                    {...register('gender')}
                    value={'OTHERS'}
                  />
                  <p className="text--bold text--semi title other">Other</p>
                </label>
              </div>
            </FieldWrapper>
          </div>
          <div className="input-container">
            <FieldWrapper label={''} error={formState.errors.email}>
              <div className="input-form">
                <input
                  type="text"
                  placeholder="Enter your email"
                  {...register('email')}
                  data-hj-allow
                />
              </div>
            </FieldWrapper>
          </div>
          <div className="input-container">
            <FieldWrapper label={''} error={formState.errors.state}>
              <div className="input-form">
                <select
                  className="select-picker select"
                  data-width="100%"
                  data-style="custom-btn"
                  title="Select your state"
                  {...register('state')}
                >
                  {states.map((eachOption: State) => (
                    <option key={eachOption.id} value={eachOption.id}>
                      {eachOption.stateName}
                    </option>
                  ))}
                </select>
              </div>
            </FieldWrapper>
          </div>
          <div className="action-container">
            <button
              type="submit"
              className="custom__button custom__button--dark text--bold w-100"
            >
              Continue
            </button>
          </div>
          <p
            className="skipbtn text-center"
            onClick={() => {
              closeModal();
            }}
          >
            <button className="text--darkBlue text--semi" data-dismiss="modal">
              Skip
            </button>
          </p>
        </div>
      )}
    </Form>
  );
};

export default UserDetails;
